import { BanIcon, CheckCircle2Icon, ChevronDownIcon, LanguagesIcon, Layers3Icon, PanelRightCloseIcon, PlusIcon, TagIcon, Trash2Icon, UsersIcon, XIcon } from 'lucide-react';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { toast } from 'sonner';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { TagInput } from '@/components/TagInput';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useTenantSettings } from '@/features/org/queries';
import { errorMessage } from '@/lib/api';
import { useMe } from '@/lib/auth';
import type { ContactDto } from '@/lib/types';
import { cn, initials } from '@/lib/utils';
import { ContactEditor } from './ContactForm';
import { useBulkDeleteContacts, useBulkTags, useTags, useUpdateContact } from './queries';

/** What the rail is showing: a contact's id, the new-contact form, or the bulk editor. */
export type RailKey = string | 'new' | 'bulk';

/**
 * Contents of the app's right rail while the contacts page is open (see components/RailOutlet for
 * the pinned, resizable panel itself and its drawer form on phones). Every opened contact becomes
 * a tab (click a name to add one), "New contact" is a tab, and once two or more rows are ticked a
 * "Bulk edit" tab appears with tag, language, status and delete actions for the whole selection.
 */
export function ContactRail({
  jobId,
  open,
  newOpen,
  active,
  selected,
  onActivate,
  onClose,
  onCloseAll,
  onSaved,
  onDeselect,
  onClearSelection,
}: {
  jobId: string;
  /** Open contact tabs, in order. */
  open: ContactDto[];
  newOpen: boolean;
  active: RailKey | null;
  /** Ticked rows, for the bulk tab. */
  selected: ContactDto[];
  onActivate: (key: RailKey) => void;
  onClose: (key: RailKey) => void;
  onCloseAll: () => void;
  onSaved: (fromKey: RailKey, saved?: ContactDto) => void;
  onDeselect: (id: string) => void;
  onClearSelection: () => void;
}) {
  const showBulk = selected.length >= 2;
  const tabs: RailTab[] = [
    ...(showBulk ? [{ key: 'bulk' as RailKey, label: `Bulk edit · ${selected.length}`, mark: <Layers3Icon className="size-3.5" aria-hidden />, closable: false }] : []),
    ...(newOpen ? [{ key: 'new' as RailKey, label: 'New contact', mark: <PlusIcon className="size-3.5" aria-hidden />, closable: true }] : []),
    ...open.map((c) => ({ key: c.id as RailKey, label: c.fullName || c.phoneDisplay, mark: <span className="bg-muted text-muted-foreground grid size-5 place-items-center text-[9px] font-semibold">{initials(c.fullName || c.phoneDisplay)}</span>, closable: true })),
  ];
  const current = tabs.some((t) => t.key === active) ? active : (tabs[0]?.key ?? null);
  const activeContact = typeof current === 'string' && current !== 'new' && current !== 'bulk' ? open.find((c) => c.id === current) : undefined;

  return (
    <div className="flex h-full min-h-0 flex-col">
      <RailTabs tabs={tabs} current={current} onActivate={onActivate} onClose={onClose} onCloseAll={onCloseAll} />

      {/* Body */}
      <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
        {current === 'bulk' ? (
          <BulkEditPanel jobId={jobId} contacts={selected} onDeselect={onDeselect} onClear={onClearSelection} />
        ) : current === 'new' ? (
          <ContactEditor key="new" jobId={jobId} onDone={(saved) => onSaved('new', saved)} onCancel={() => onClose('new')} />
        ) : activeContact ? (
          <ContactEditor key={activeContact.id} jobId={jobId} contact={activeContact} onDone={(saved) => onSaved(activeContact.id, saved)} onCancel={() => onClose(activeContact.id)} autoFocus={false} />
        ) : (
          <div className="text-muted-foreground flex flex-1 flex-col items-center justify-center gap-3 px-6 py-16 text-center text-sm">
            <UsersIcon className="size-6" aria-hidden />
            <p>Click a name to open it here. Each one gets a tab, so you can work through several without losing your place.</p>
            <p className="text-xs">Tick two or more rows to edit them together.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------------------------ tabs

interface RailTab {
  key: RailKey;
  label: string;
  mark: ReactNode;
  closable: boolean;
}

/** Every tab is this wide so the overflow arithmetic is exact. */
const TAB_W = 136;
const MORE_W = 84;

/**
 * Browser-style tab strip. Tabs that do not fit collapse into a "More" menu; the active tab always
 * stays visible (it swaps into the last slot when it would otherwise be hidden).
 */
function RailTabs({ tabs, current, onActivate, onClose, onCloseAll }: { tabs: RailTab[]; current: RailKey | null; onActivate: (k: RailKey) => void; onClose: (k: RailKey) => void; onCloseAll: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    setWidth(el.clientWidth);
    const ro = new ResizeObserver(([entry]) => setWidth(entry?.contentRect.width ?? el.clientWidth));
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // The strip's own padding is already outside `width`; each tab takes TAB_W plus the 4px gap.
  const fitAll = width > 0 ? Math.floor((width + 4) / (TAB_W + 4)) : tabs.length;
  const fit = fitAll >= tabs.length ? tabs.length : Math.max(1, Math.floor((width - MORE_W + 4) / (TAB_W + 4)));
  let visible = tabs.slice(0, fit);
  const activeIdx = tabs.findIndex((t) => t.key === current);
  if (activeIdx >= fit) visible = [...tabs.slice(0, Math.max(0, fit - 1)), tabs[activeIdx]!];
  const hidden = tabs.filter((t) => !visible.includes(t));

  return (
    <div className="bg-muted/40 flex shrink-0 items-end border-b">
      <div ref={ref} className="flex min-w-0 flex-1 items-end gap-1 px-2 pt-2" role="tablist" aria-label="Open contacts">
        {visible.map((t) => {
          const isActive = t.key === current;
          return (
            <div
              key={t.key}
              className={cn(
                'group relative flex h-9 shrink-0 items-center border border-b-0 pr-1 pl-2.5 text-sm transition-colors',
                isActive ? 'bg-card text-foreground -mb-px' : 'text-muted-foreground hover:bg-muted/70 hover:text-foreground border-transparent',
              )}
              style={{ width: TAB_W }}
            >
              <button type="button" role="tab" aria-selected={isActive} onClick={() => onActivate(t.key)} className="flex min-w-0 flex-1 items-center gap-2 text-left outline-none">
                {t.mark}
                <span className={cn('truncate', isActive && 'font-medium')}>{t.label}</span>
              </button>
              {t.closable ? (
                <button
                  type="button"
                  onClick={() => onClose(t.key)}
                  aria-label={`Close ${t.label}`}
                  className={cn('hover:bg-foreground/10 grid size-6 shrink-0 place-items-center transition-opacity', isActive ? 'opacity-70 hover:opacity-100' : 'opacity-0 group-hover:opacity-70 focus-visible:opacity-100')}
                >
                  <XIcon className="size-3.5" />
                </button>
              ) : (
                <span className="size-6 shrink-0" aria-hidden />
              )}
            </div>
          );
        })}
        {hidden.length > 0 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button type="button" className="text-muted-foreground hover:text-foreground flex h-9 shrink-0 items-center gap-1 px-2.5 text-xs font-medium tabular-nums transition-colors" aria-label={`${hidden.length} more open contacts`}>
                {hidden.length} more <ChevronDownIcon className="size-3.5" aria-hidden />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-64">
              {hidden.map((t) => (
                <DropdownMenuItem key={t.key} onSelect={() => onActivate(t.key)} className="gap-2">
                  {t.mark}
                  <span className="truncate">{t.label}</span>
                </DropdownMenuItem>
              ))}
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={onCloseAll}>
                <PanelRightCloseIcon /> Close all
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      {tabs.length > 0 && (
        <button type="button" onClick={onCloseAll} className="text-muted-foreground hover:text-foreground grid size-10 shrink-0 place-items-center border-l transition-colors" aria-label="Close panel" title="Close all">
          <PanelRightCloseIcon className="size-4" />
        </button>
      )}
    </div>
  );
}

// ------------------------------------------------------------------------------------ bulk edit

/** Actions that apply to every ticked contact at once. */
export function BulkEditPanel({ jobId, contacts, onDeselect, onClear }: { jobId: string; contacts: ContactDto[]; onDeselect: (id: string) => void; onClear: () => void }) {
  const me = useMe();
  const settings = useTenantSettings(true);
  const tags = useTags(jobId);
  const bulkTags = useBulkTags(jobId);
  const bulkDel = useBulkDeleteContacts(jobId);
  const update = useUpdateContact(jobId);
  const [add, setAdd] = useState<string[]>([]);
  const [remove, setRemove] = useState<string[]>([]);
  const [language, setLanguage] = useState('');
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [confirm, setConfirm] = useState<'delete' | 'optout' | 'subscribe' | null>(null);
  const ids = contacts.map((c) => c.id);
  const showLanguage = me.tenant.features.translation || me.tenant.i18n.enabledLanguages.length > 1;
  const langName = (code: string) => settings.data?.languages.find((l) => l.code === code)?.name ?? code.toUpperCase();
  const busy = bulkTags.isPending || bulkDel.isPending || progress !== null;
  const optedOut = contacts.filter((c) => c.optedOut).length;

  /** Run a per-contact PATCH across the selection, reporting progress. */
  const each = async (label: string, body: Parameters<typeof update.mutateAsync>[0]['body'], only?: (c: ContactDto) => boolean) => {
    const targets = only ? contacts.filter(only) : contacts;
    setProgress({ done: 0, total: targets.length });
    let ok = 0;
    for (const c of targets) {
      try {
        await update.mutateAsync({ contactId: c.id, body });
        ok++;
      } catch {
        /* keep going; the summary says how many worked */
      }
      setProgress({ done: ok, total: targets.length });
    }
    setProgress(null);
    if (ok === targets.length) toast.success(`${label} for ${ok} ${ok === 1 ? 'contact' : 'contacts'}`);
    else toast.warning(`${label} for ${ok} of ${targets.length} contacts; the rest failed`);
  };

  return (
    <div className="grid gap-5 p-4">
      <div>
        <div className="mb-2 flex items-center justify-between">
          <Label>
            {contacts.length} selected{optedOut > 0 ? <span className="text-muted-foreground font-normal"> · {optedOut} opted out</span> : null}
          </Label>
          <button type="button" onClick={onClear} className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline">
            Clear selection
          </button>
        </div>
        <div className="flex max-h-32 flex-wrap gap-1 overflow-y-auto">
          {contacts.map((c) => (
            <span key={c.id} className="bg-muted inline-flex h-7 items-center gap-1 pr-1 pl-2 text-xs">
              <span className="max-w-[160px] truncate">{c.fullName || c.phoneDisplay}</span>
              <button type="button" onClick={() => onDeselect(c.id)} className="hover:bg-foreground/10 grid size-5 place-items-center" aria-label={`Deselect ${c.fullName}`}>
                <XIcon className="size-3" />
              </button>
            </span>
          ))}
        </div>
      </div>

      <Section icon={TagIcon} title="Tags">
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label className="text-xs">Add</Label>
            <TagInput value={add} onChange={setAdd} suggestions={(tags.data?.items ?? []).map((t) => t.tag)} />
          </div>
          <div className="grid gap-1.5">
            <Label className="text-xs">Remove</Label>
            <TagInput value={remove} onChange={setRemove} suggestions={(tags.data?.items ?? []).map((t) => t.tag)} placeholder="Tags to remove" />
          </div>
          <Button
            size="sm"
            className="justify-self-start"
            disabled={busy || (add.length === 0 && remove.length === 0)}
            onClick={async () => {
              try {
                const r = await bulkTags.mutateAsync({ ids, add, remove });
                toast.success(`Updated tags on ${r.updated} contacts`);
                setAdd([]);
                setRemove([]);
              } catch (err) {
                toast.error(errorMessage(err));
              }
            }}
          >
            Apply tags
          </Button>
        </div>
      </Section>

      {showLanguage && (
        <Section icon={LanguagesIcon} title="Preferred language">
          <div className="flex items-center gap-2">
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="flex-1" aria-label="Language for the selection">
                <SelectValue placeholder="Choose a language" />
              </SelectTrigger>
              <SelectContent>
                {me.tenant.i18n.enabledLanguages.map((code) => (
                  <SelectItem key={code} value={code}>
                    {langName(code)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Button size="sm" disabled={busy || !language} onClick={() => void each('Language set', { language })}>
              Apply
            </Button>
          </div>
        </Section>
      )}

      <Section icon={CheckCircle2Icon} title="Status">
        <div className="flex flex-wrap gap-2">
          <Button size="sm" variant="outline" disabled={busy || optedOut === 0} onClick={() => setConfirm('subscribe')}>
            <CheckCircle2Icon /> Mark subscribed
          </Button>
          <Button size="sm" variant="outline" disabled={busy || optedOut === contacts.length} onClick={() => setConfirm('optout')}>
            <BanIcon /> Mark opted out
          </Button>
        </div>
        <p className="text-muted-foreground mt-2 text-xs">Re-subscribing applies only to people who asked to hear from you again; re-adding someone who texted STOP breaks carrier rules.</p>
      </Section>

      <Section icon={Trash2Icon} title="Remove">
        <Button size="sm" variant="outline" className="text-destructive" disabled={busy} onClick={() => setConfirm('delete')}>
          <Trash2Icon /> Delete {contacts.length} {contacts.length === 1 ? 'contact' : 'contacts'}
        </Button>
      </Section>

      {progress && (
        <div className="bg-muted/50 border px-3 py-2 text-xs tabular-nums" role="status">
          Updating {progress.done} of {progress.total}…
        </div>
      )}

      <ConfirmDialog
        open={confirm === 'delete'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={`Delete ${contacts.length} contacts?`}
        description="They are removed from this job. This cannot be undone."
        confirmLabel="Delete"
        destructive
        loading={bulkDel.isPending}
        onConfirm={async () => {
          try {
            const r = await bulkDel.mutateAsync(ids);
            toast.success(`Deleted ${r.deleted} contacts`);
            setConfirm(null);
            onClear();
          } catch (err) {
            toast.error(errorMessage(err));
          }
        }}
      />
      <ConfirmDialog
        open={confirm === 'optout' || confirm === 'subscribe'}
        onOpenChange={(o) => !o && setConfirm(null)}
        title={confirm === 'optout' ? `Opt out ${contacts.length - optedOut} contacts?` : `Re-subscribe ${optedOut} contacts?`}
        description={
          confirm === 'optout'
            ? 'They will be skipped on every send from this job until re-subscribed. Use this when people ask to stop receiving texts through any channel.'
            : 'Only do this if these people asked to receive messages again (for example, they texted START). Re-subscribing someone who replied STOP violates carrier rules.'
        }
        confirmLabel={confirm === 'optout' ? 'Opt out' : 'Re-subscribe'}
        destructive={confirm === 'optout'}
        loading={progress !== null}
        onConfirm={async () => {
          const optout = confirm === 'optout';
          setConfirm(null);
          await each(optout ? 'Opted out' : 'Re-subscribed', { optedOut: optout }, (c) => c.optedOut !== optout);
        }}
      />
    </div>
  );
}

function Section({ icon: Icon, title, children }: { icon: typeof TagIcon; title: string; children: ReactNode }) {
  return (
    <section className="border p-3">
      <div className="mb-3 flex items-center gap-2 text-sm font-medium">
        <Icon className="text-muted-foreground size-4" aria-hidden />
        {title}
      </div>
      {children}
    </section>
  );
}
